import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import adminViewImage from '../assets/icons/light/admin-view.png';
import userViewImage from '../assets/icons/light/user-view.png';

import { 
  Box, 
  Breadcrumbs,
  Button, 
  CircularProgress, 
  Chip, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Paper, 
  Typography, 
  TextField, 
  IconButton, 
  Tooltip, 
  InputAdornment, 
  Alert, 
  Table, 
  TableBody, 
  TableCell, 
  tableCellClasses, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  Link as MuiLink,
  MenuItem,
  alpha,
  Snackbar
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachmentIcon from '@mui/icons-material/Attachment';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/material/styles';

const API_URL = process.env.REACT_APP_API_URL;

// Function to check if a user has admin privileges
// This should be replaced with an actual API call in a production implementation
const isAdminUser = (email) => {
  if (!email) return false;
  
  // The backend authentication.js has proper admin verification via the database
  // It uses the access_control table to check if the email is in the admin_emails array
  // The API endpoint /api/auth/check-admin would be the proper way to verify
  
  // For now, we'll use the domain check as a basic client-side verification
  // This matches the backend's ALLOWED_EMAIL_DOMAIN environment variable
  const adminDomain = process.env.REACT_APP_ADMIN_EMAIL_DOMAIN;
  // If adminDomain is not set, we'll return false to be safe
  return adminDomain ? email.endsWith(`@${adminDomain}`) : false;
};

// Helper function to extract filename from URL
const getFilenameFromUrl = (url) => {
  if (!url) return 'attachment';
  try {
    const parsedUrl = new URL(url);
    // Remove query parameters for a cleaner name, especially for S3 presigned URLs
    const pathname = parsedUrl.pathname;
    // Decode URI components and get the last part of the path
    const filename = decodeURIComponent(pathname.substring(pathname.lastIndexOf('/') + 1));
    return filename || 'attachment';
  } catch (e) {
    // Fallback for invalid URLs or if URL parsing fails
    const parts = url.split('/');
    const lastPart = parts.pop() || parts.pop(); // Handle trailing slash
    const queryParamIndex = lastPart ? lastPart.indexOf('?') : -1;
    return queryParamIndex !== -1 ? lastPart.substring(0, queryParamIndex) : lastPart || 'attachment';
  }
};

// Component to render each attachment
const AttachmentItem = ({ file }) => {
  const [imgError, setImgError] = useState(false);
  const filename = getFilenameFromUrl(file.url || file); // Handle both object and string URLs
  const url = file.url || file;

  const isImage = /\.(jpeg|jpg|gif|png|svg)$/i.test(filename);

  if (isImage && !imgError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
        <img 
          src={url} 
          alt={filename} 
          style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'cover', cursor: 'pointer' }} 
          onClick={() => window.open(url, '_blank')}
          onError={() => setImgError(true)} 
        />
        <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '5px', textDecoration: 'none' }}>
          <AttachmentIcon sx={{ fontSize: '16px' }} />
          {filename}
        </Link>
      </Box>
    );
  }

  return (
    <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: '5px', textDecoration: 'none', padding: '8px', border: '1px solid #eee', borderRadius: '4px', '&:hover': { backgroundColor: '#f9f9f9'} }}>
      <AttachmentIcon sx={{ fontSize: '18px' }} />
      {filename}
    </Link>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    height: '63px',
    padding: '8px 16px',
    verticalAlign: 'middle',
    borderBottom: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    borderBottom: '1.2px solid #e0e0e0',
    padding: '8px 16px',
    height: '63px',
    verticalAlign: 'middle',
  },
}));

const StyledTableRow = styled(TableRow)({
  '&:last-child td': {
    borderBottom: 'none',
  },
  '&:last-child td:first-of-type': {
    borderBottomLeftRadius: '8px',
  },
  '&:last-child td:last-child': {
    borderBottomRightRadius: '8px',
  },
  '&:hover td': {
    backgroundColor: '#f5f5f5',
  },
});

const PriorityChip = styled(Chip)(({ priority }) => {
  const colors = {
    'Critical': { bg: '#FFE5E5', color: '#DC1D1D' },
    'High': { bg: '#FFE5E5', color: '#ECA00D' },
    'Medium': { bg: '#FFE5E5', color: '#F1C100' },
    'Low': { bg: '#E5F8F0', color: '#5FBDF1' }
  };
  
  const defaultColor = { bg: '#e0e0e0', color: '#616161' };
  const colorSet = colors[priority] || defaultColor;
  
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    fontWeight: 700,
    '& .MuiChip-label': {
      padding: '0 8px',
    }
  };
});

export default function TrackTickets({ isAdmin: propIsAdmin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [ticketStatus, setTicketStatus] = useState({});
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showPopup, setShowPopup] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTicket, setEditedTicket] = useState({});
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAdminState, setIsAdminState] = useState(propIsAdmin || false);
  const token = localStorage.getItem('auth_token');
  
  // Update isAdminState when prop changes
  useEffect(() => {
    if (propIsAdmin !== undefined) {
      setIsAdminState(propIsAdmin);
    }
  }, [propIsAdmin]);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userEmail = localStorage.getItem('user_email');
    
    if (!token || !userEmail) {
      navigate('/login');
      return;
    }
    
    setUser({ email: userEmail });
    
    // Check if user is admin
    checkAdminStatus();
    fetchTickets();
    fetchUsers();
  }, [navigate]);
  
  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${API_URL}/auth/check-admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Only store admin status in component state, not in localStorage
      setIsAdminState(response.data.isAdmin);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdminState(false);
    }
  };

  // Handle direct ticket URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const ticketNumberFromUrl = pathParts[pathParts.length - 1];
    
    // Only proceed if we have tickets and the last part of the URL is a number
    if (tickets.length > 0 && !isNaN(ticketNumberFromUrl) && ticketNumberFromUrl !== 'tickets') {
      const ticket = tickets.find(t => t.ticket_number.toString() === ticketNumberFromUrl);
      if (ticket && (!selectedTicket || selectedTicket.ticket_number.toString() !== ticketNumberFromUrl)) {
        handleViewTicket(ticket);
      }
    }
  }, [location.pathname, tickets, selectedTicket]);

  useEffect(() => {
    if (tickets && tickets.length > 0) {
      filterTickets(searchTerm);
    } else {
      setFilteredTickets([]);
    }
  }, [searchTerm, tickets]);

  const fetchTickets = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('auth_token');
      
      const response = await axios.get(`${API_URL}/tickets/user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Check if response.data is an object with a tickets property
      let ticketsData = [];
      if (response.data && response.data.tickets && Array.isArray(response.data.tickets)) {
        console.log('Tickets received:', response.data.tickets.length);
        ticketsData = response.data.tickets;
      } else if (Array.isArray(response.data)) {
        console.log('Tickets received (direct array):', response.data.length);
        ticketsData = response.data;
      } else {
        console.error('Expected array or object with tickets array but got:', typeof response.data);
        setTickets([]);
        setFilteredTickets([]);
        setError('Invalid data format received from server');
        return;
      }
      
      // Sort tickets by ticket_number in ascending order
      const sortedTickets = [...ticketsData].sort((a, b) => {
        // Convert to numbers for proper numeric sorting
        const numA = parseInt(a.ticket_number, 10) || 0;
        const numB = parseInt(b.ticket_number, 10) || 0;
        return numA - numB;
      });
      
      setTickets(sortedTickets);
      setFilteredTickets(sortedTickets);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to load tickets. Please try again.');
      setTickets([]);
      setFilteredTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      // Step 1: Fetch all admin emails
      const accessControlResponse = await axios.get(`${API_URL}/auth/admin-emails`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const adminEmails = accessControlResponse.data.adminEmails || [];
  
      // Step 2: Format for dropdown (email only)
      const adminUserObjects = adminEmails.map(email => ({ email }));
  
      setUsers(adminUserObjects);  // Only admin users shown in assignees dropdown
    } catch (error) {
      console.error('Error fetching admin emails:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };
  


  const filterTickets = (searchTerm) => {
    if (!searchTerm) {
      setFilteredTickets(tickets);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    
    const filtered = tickets.filter(ticket => {
      // Check if any assignee matches the search term
      const assigneeMatch = ticket.assignees && (
        (typeof ticket.assignees === 'string' && ticket.assignees.toLowerCase().includes(term)) ||
        (Array.isArray(ticket.assignees) && ticket.assignees.some(assignee => 
          assignee && assignee.toLowerCase().includes(term)
        ))
      );
      
      return (
        (ticket.ticket_number && ticket.ticket_number.toString().includes(term)) ||
        (ticket.title && ticket.title.toLowerCase().includes(term)) ||
        (ticket.summary && ticket.summary.toLowerCase().includes(term)) ||
        (ticket.description && ticket.description.toLowerCase().includes(term)) ||
        (ticket.status && ticket.status.toLowerCase().includes(term)) ||
        (ticket.priority && ticket.priority.toLowerCase().includes(term)) ||
        (ticket.user_email && ticket.user_email.toLowerCase().includes(term)) ||
        assigneeMatch ||
        (ticket.additional_users && Array.isArray(ticket.additional_users) && ticket.additional_users.some(user => user.toLowerCase().includes(term)))
      );
    });
    
    setFilteredTickets(filtered);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewTicket = (ticket) => {
    // Ensure assignees is always an array
    const processedTicket = {
      ...ticket,
      assignees: Array.isArray(ticket.assignees) ? ticket.assignees :
                (ticket.assignees ? ticket.assignees.split(',') : [])
    };
    console.log('Viewing ticket:', processedTicket);
    setSelectedTicket(processedTicket);
    setOpenDialog(true);
    
    // Update URL to include ticket number
    navigate(`/tickets/${ticket.ticket_number}`, { replace: true });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Clear the ticket number from URL when dialog is closed
    navigate('/tickets', { replace: true });
  };

  const handleTicketSelect = (ticket) => {
    // Ensure assignees is always an array
    const processedTicket = {
      ...ticket,
      assignees: Array.isArray(ticket.assignees) ? ticket.assignees :
                (ticket.assignees ? ticket.assignees.split(',') : [])
    };
    setSelectedTicket(processedTicket);
    setEditedTicket(processedTicket);
    setOpenEditDialog(true);
  };

  const handleStatusChange = (e, ticketNumber) => {
    setTicketStatus(prevStatus => ({ ...prevStatus, [ticketNumber]: e.target.value }));
  };

  const saveStatus = async (ticketNumber) => {
    try {
      const newStatus = ticketStatus[ticketNumber];
      
      if (!newStatus) {
        console.log('No status change to save');
        return;
      }
      
      console.log(`Saving status for ticket ${ticketNumber}: ${newStatus}`);
      
      if (!token) {
        console.error('Authentication token is missing. Please log in again.');
        navigate('/login');
        return;
      }
      
      console.log('Request URL:', `${API_URL}/tickets/${ticketNumber}/status`);
      
      setLoading(true);
      
      const response = await axios.put(
        `${API_URL}/tickets/${ticketNumber}/status`,
        { status: newStatus },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      console.log('Status update response:', response.data);
      
      // Update the ticket in the tickets list
      const updatedTickets = tickets.map(t => 
        t.ticket_number === ticketNumber 
          ? { ...t, status: newStatus }
          : t
      );
      
      setTickets(updatedTickets);
      setFilteredTickets(updatedTickets);
      
      // Update the selected ticket if it's the same one
      if (selectedTicket && selectedTicket.ticket_number === ticketNumber) {
        setSelectedTicket(prev => ({...prev, status: newStatus}));
      }
      
      // Clear the status change from the tracking object
      setTicketStatus(prev => {
        const newState = { ...prev };
        delete newState[ticketNumber];
        return newState;
      });
      
      console.log('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      
      // Only show an alert for errors, not a popup
      alert(`Failed to update status: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedTicket(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveEditedTicket = async () => {
    try {
      setLoading(true);
      
      // Ensure we have the required fields
      if (!editedTicket.title || !editedTicket.description) {
        setMessage('Please fill in all required fields');
        setMessageType('error');
        setShowPopup(true);
        setLoading(false);
        return;
      }
      
      // Ensure assignees is in the right format
      let assignees = editedTicket.assignees;
      if (!Array.isArray(assignees)) {
        assignees = assignees ? assignees.split(',').map(email => email.trim()).filter(email => email) : [];
      }
      
      // Prepare the data for the API
      const ticketData = {
        title: editedTicket.title,
        summary: editedTicket.summary || '',
        description: editedTicket.description,
        status: editedTicket.status || 'Open',
        priority: editedTicket.priority || 'Medium',
        assignees: assignees,
        comments: editedTicket.comments || ''
      };
      
      console.log('Sending ticket update:', ticketData);
      
      // Make the API call to update the ticket
      await axios.put(
        `${API_URL}/tickets/${editedTicket.ticket_number}/update`,
        ticketData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Fetch fresh data from the server
      await fetchTickets();
      
      // Show success message
      setMessage('Ticket updated successfully');
      setMessageType('success');
      setShowPopup(true);
      
      // Close the edit dialog
      setOpenEditDialog(false);
      setEditMode(false);
    } catch (error) {
      console.error('API Error updating ticket:', error);
      
      // Even if the API call fails with a 500 error, update the UI if the assignees were the only change
      // This is a workaround for the server-side issue
      if (error.response && error.response.status === 500) {
        // Check if the error is related to the assignees field
        const errorMessage = error.response.data?.error || '';
        if (errorMessage.includes('assignees') || editedTicket.assignees) {
          // Fetch fresh data from the server
          await fetchTickets();
          
          setMessage('Ticket may have been updated. Please check the updated assignees.');
          setMessageType('warning');
          setShowPopup(true);
          
          // Close the edit dialog
          setOpenEditDialog(false);
          setEditMode(false);
        } else {
          setMessage('Failed to update ticket');
          setMessageType('error');
          setShowPopup(true);
        }
      } else {
        setMessage('Failed to update ticket');
        setMessageType('error');
        setShowPopup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssigneeChange = async (event) => {
    try {
      let assignees = event.target.value;
      // Ensure assignees is always an array
      if (!Array.isArray(assignees)) {
        assignees = assignees ? assignees.split(',').map(email => email.trim()).filter(email => email) : [];
      }
      
      setSelectedTicket({
        ...selectedTicket,
        assignees: assignees
      });
      
      setLoading(true);
      await axios.put(
        `${API_URL}/tickets/${selectedTicket.ticket_number}/update`,
        { assignees },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Fetch fresh data from the server
      await fetchTickets();
      
      setMessage('Ticket assigned successfully');
      setMessageType('success');
      setShowPopup(true);
    } catch (error) {
      console.error('Error assigning ticket:', error);
      
      // Revert the local state change
      setSelectedTicket({
        ...selectedTicket,
        assignees: selectedTicket.assignees
      });
      
      setMessage('Failed to assign ticket');
      setMessageType('error');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const closePopup = () => {
    setShowPopup(false);
    setEditMode(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const extension = filename.split('.').pop().toLowerCase();
    return imageExtensions.includes(extension);
  };

  const ticketsToDisplay = Array.isArray(filteredTickets) ? filteredTickets : [];
  const paginatedTickets = ticketsToDisplay.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div>
      <Snackbar
        open={showPopup}
        autoHideDuration={6000}
        onClose={() => {
          setShowPopup(false);
          setMessage('');
          setMessageType('info');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => {
            setShowPopup(false);
            setMessage('');
            setMessageType('info');
          }} 
          severity={messageType}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
      
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh', 
          backgroundColor: '#EEF5F6',
          paddingTop: 1,  // Reduced from 3
          paddingBottom: 4,  // Reduced from 6
          width: '100%',
          margin: 0,
          paddingX: 2
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '1400px', margin: '0 auto', px: 3 }}>
          <Box sx={{ mb: 2, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              component={Link}
              to="/support-dashboard"
              sx={{
                color: '#4299D4',
                fontFamily: 'Proxima Nova',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                textAlign: 'right',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'none'
                }
              }}
            >
              &lt; Back
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#5D5D5D' }}>
              <MuiLink
                component={Link}
                to="/"
                sx={{ 
                  color: '#4299D4',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }}
              >
                Dashboard
              </MuiLink>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="#4299D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <MuiLink
                component={Link}
                to="/support-dashboard"
                sx={{ 
                  color: '#4299D4',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }}
              >
                Support Management
              </MuiLink>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="#4299D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={{ color: '#6D6D6D' }}>Track your Tickets</span>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pt: 1 }}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{
                color: '#1674B4',
                fontFamily: 'Proxima Nova',
                fontSize: '30px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
                margin: 0,
                marginLeft: '0'
              }}
            >
              Track Your Tickets
            </Typography>
            {isAdminState ? (
              <img 
                src={adminViewImage} 
                alt="Admin View" 
                style={{ 
                  width: '193.44px', 
                  height: '27px',
                  marginRight: '0'
                }} 
              />
            ) : (
              <img 
                src={userViewImage} 
                alt="User View" 
                style={{ 
                  width: '191.442px', 
                  height: '22px',
                  marginRight: '0'
                }} 
              />
            )}
          </Box>
          
          {/* Divider under the title */}
          <Box 
            sx={{ 
              width: '100%',
              height: '1px',
              backgroundColor: '#B1B1B1',
              my: 2
            }} 
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, width: '100%', maxWidth: '1400px', margin: '0 auto', px: 3 }}>
          <Box sx={{ maxWidth: '50%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'black', mb: 0.5, lineHeight: 1.0 }}>
              View and manage all your support tickets in one place.
            </Typography>
            <Typography variant="body2" sx={{ color: 'black', lineHeight: 1.0}}>
              Check ticket status, update progress, and track assignments in real time.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              label="Search Tickets"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ marginTop: '4px' }}>
                    <SearchIcon sx={{ color: '#0FBDA8' }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => {
                        setSearchTerm('');
                        setFilteredTickets(tickets);
                      }}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: '314px',
                '& .MuiOutlinedInput-root': {
                  height: '35px',
                  borderRadius: '24px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#5EB0E8' // Keep border color consistent on hover
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '24px',
                  borderColor: '#5EB0E8',
                  borderWidth: '1px'
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0, 0, 0, 0.6)'
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#5EB0E8'
                },
                '& .MuiOutlinedInput-input': {
                  '&:hover, &:focus': {
                    backgroundColor: 'transparent' // Remove any hover/focus background
                  }
                }
              }}
            />
            <Tooltip title="Refresh Tickets">
              <Box 
                component="img"
                src={require('../assets/icons/light/refresh-outline.png')}
                alt="Refresh"
                onClick={fetchTickets}
                sx={{
                  width: '40px',
                  height: '40px',
                  flexShrink: 0,
                  aspectRatio: '1/1',
                  cursor: 'pointer',
                  marginTop: '4px',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              />
            </Tooltip>
          </Box>
        </Box>
        
        {/* Add space between controls and table */}
        <Box sx={{ mb: 2 }} />
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ maxWidth: '1400px', margin: '0 auto', px: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : tickets.length === 0 ? (
          <Box sx={{ maxWidth: '1400px', margin: '0 auto', px: 3 }}>
            <Alert severity="info">No tickets found. Create a new ticket to get started.</Alert>
          </Box>
        ) : (
          <Box sx={{ width: '100%', mb: 4, position: 'relative', maxWidth: '1400px', margin: '0 auto', px: 3 }}>
            <TableContainer 
              component={Paper} 
              elevation={3} 
              sx={{ 
                width: '100%', 
                overflowX: 'auto', 
                borderRadius: '8px',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderBottomLeftRadius: '8px',
                  borderBottomRightRadius: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              <Table stickyHeader aria-label="tickets table" size="small">
                <TableHead>
                  <TableRow sx={{
                    '& th:first-of-type': {
                      borderTopLeftRadius: '8px',
                    },
                    '& th:last-child': {
                      borderTopRightRadius: '8px',
                    },
                  }}>
                    <StyledTableCell sx={{ width: '100px' }}>Ticket</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: '200px' }}>Title</StyledTableCell>
                    <StyledTableCell sx={{ width: '150px' }}>User</StyledTableCell>
                    <StyledTableCell sx={{ width: '150px' }}>Status</StyledTableCell>
                    <StyledTableCell sx={{ width: '100px' }}>Priority</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: '200px' }}>Assignees</StyledTableCell>
                    <StyledTableCell sx={{ minWidth: '150px' }}>Additional Users</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTickets.map((ticket, index) => {
                    // Process assignees list
                    let assigneesList = [];
                    if (ticket.assignees) {
                      if (Array.isArray(ticket.assignees)) {
                        assigneesList = ticket.assignees;
                      } else if (typeof ticket.assignees === 'string') {
                        assigneesList = ticket.assignees.split(',').map(a => a.trim()).filter(a => a);
                      }
                    }
                    const isCurrentUserAssigned = user && user.email && assigneesList.includes(user.email);
                    
                    return (
                      <StyledTableRow 
                        key={ticket.ticket_number}
                        hover
                        sx={{ 
                          backgroundColor: isCurrentUserAssigned ? alpha('#4caf50', 0.1) : 'inherit',
                          '&:hover': {
                            backgroundColor: isCurrentUserAssigned ? alpha('#4caf50', 0.2) : alpha('#bbdefb', 0.2),
                          }
                        }}
                      >
                        <StyledTableCell>
                        <Typography 
                          color="primary" 
                          fontWeight="medium"
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => handleViewTicket(ticket)}
                        >
                          {page * rowsPerPage + index + 1}
                        </Typography>

                        </StyledTableCell>
                        <StyledTableCell>{ticket.title}</StyledTableCell>
                        <StyledTableCell>{ticket.user_email}</StyledTableCell>
                        <StyledTableCell sx={{ display: 'none' }}>{ticket.ticket_number}</StyledTableCell>
                        <StyledTableCell>
                        {isAdminState ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <select 
                              value={ticketStatus[ticket.ticket_number] || ticket.status} 
                              onChange={(e) => handleStatusChange(e, ticket.ticket_number)}
                              style={{ 
                                width: '96px',
                                height: '35px',
                                padding: '4px 8px',
                                borderRadius: '4px', 
                                border: '1px solid #B1B1B1',
                                boxSizing: 'border-box',
                                background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='6' viewBox='0 0 11 6' fill='none'%3E%3Cpath d='M1 1L5.5 5L10 1' stroke='%236D6D6D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 8px center/10.973px 5.486px`,
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                paddingRight: '24px',
                                backgroundColor: 'white'
                              }}
                            >
                              <option value="Open">Open</option>
                              <option value="In Progress">In Progress</option>
                              <option value="Resolved">Resolved</option>
                              <option value="Closed">Closed</option>
                            </select>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{
                                backgroundColor: '#2DB9A8',
                                '&:hover': {
                                  backgroundColor: '#2DB9A8',
                                  boxShadow: 'none'
                                },
                                '&:active': {
                                  backgroundColor: '#2DB9A8',
                                  boxShadow: 'none'
                                },
                                boxShadow: 'none'
                              }}
                              onClick={() => saveStatus(ticket.ticket_number)}
                            >
                              Save
                            </Button>
                          </Box>
                        ) : (
                          <Typography fontWeight="bold">{ticket.status}</Typography>
                        )}
                      </StyledTableCell>

                        <StyledTableCell>
                          <PriorityChip 
                            label={ticket.priority} 
                            priority={ticket.priority}
                            size="small"
                          />
                        </StyledTableCell>
                        <StyledTableCell>
                          {assigneesList.length > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {assigneesList.map(assignee => (
                                <Chip 
                                  key={assignee} 
                                  label={assignee} 
                                  size="small"
                                  color={user && (user.email === assignee || isAdminState) ? "success" : "default"}
                                  deleteIcon={user && (user.email === assignee || isAdminState) ? <EditIcon /> : null}
                                  onDelete={user && (user.email === assignee || isAdminState) ? 
                                    (e) => {
                                      e.stopPropagation();
                                      setEditedTicket(ticket);
                                      setEditMode(true);
                                      setOpenDialog(false);
                                      setOpenEditDialog(true);
                                    } : null
                                  }
                                />
                              ))}
                            </Box>
                          ) : (
                            'Unassigned'
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {ticket.additional_users && Array.isArray(ticket.additional_users) && ticket.additional_users.length > 0
                            ? ticket.additional_users.join(', ')
                            : 'None'}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                <tfoot>
                  <TableRow>
                    <StyledTableCell colSpan={7} sx={{
                      backgroundColor: '#F3F3F3', // BK2 color
                      padding: 0,
                      borderBottom: 'none',
                      borderRadius: '0 0 8px 8px',
                      boxShadow: '2px 2px 2px 0px rgba(0, 0, 0, 0.25)',
                      height: '63px',
                      verticalAlign: 'middle',
                      '& .MuiTablePagination-root': {
                        width: '100%',
                        margin: 0,
                        borderRadius: '0 0 8px 8px',
                      },
                      '& .MuiTablePagination-toolbar': {
                        minHeight: '63px',
                        height: '63px',
                        padding: '0 16px',
                        backgroundColor: 'transparent',
                        borderRadius: '0 0 8px 8px',
                        color: '#2E2E2E', // Dark text for contrast on light background
                      },
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon': {
                        color: '#2E2E2E',
                      },
                      '& .MuiSvgIcon-root': {
                        fill: '#4299D4', // G5 color for chevrons
                      },
                      '& .Mui-disabled .MuiSvgIcon-root': {
                        fill: 'rgba(0, 0, 0, 0.26)', // Disabled state color
                      },
                      '& .MuiIconButton-root': {
                        color: '#4299D4', // G5 color for icon buttons
                        '&:hover': {
                          backgroundColor: 'rgba(66, 153, 212, 0.08)', // Light hover effect
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.26)', // Disabled state
                        }
                      }
                    }}>
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 15]}
                        component="div"
                        count={filteredTickets.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Tickets per page:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                      />
                    </StyledTableCell>
                  </TableRow>
                </tfoot>
              </Table>
            </TableContainer>
          </Box>
        )}
        
        {/* Ticket Detail Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          {selectedTicket && (
            <>
              <DialogTitle>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
                  <h2 style={{ margin: 0 }}>Ticket {selectedTicket.ticket_number}</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {(user && (isAdminState || user.email === selectedTicket.user_email || 
   (Array.isArray(selectedTicket.assignees) && selectedTicket.assignees.includes(user.email)) || 
   (Array.isArray(selectedTicket.additional_users) && selectedTicket.additional_users.includes(user.email)))) && (
                      <Button 
                        variant="contained" 
                        color="primary" 
                        size="small"
                        onClick={() => {
                          setEditedTicket(selectedTicket);
                          setOpenDialog(false);
                          setOpenEditDialog(true);
                        }}
                        startIcon={<EditIcon />}
                      >
                        Edit Ticket
                      </Button>
                    )}
                    <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>
              </DialogTitle>
              <DialogContent dividers>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <div>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Title:</label>
                    <p style={{ marginBottom: "15px" }}>{selectedTicket.title}</p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Description:</label>
                    <p style={{ 
                      marginBottom: "15px", 
                      whiteSpace: "pre-wrap", 
                      backgroundColor: "#f9f9f9", 
                      padding: "10px", 
                      borderRadius: "4px" 
                    }}>
                      {selectedTicket.description}
                    </p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Status:</label>
                    <p style={{ 
                      marginBottom: "15px",
                      color: 
                        selectedTicket.status === "Open" ? "blue" : 
                        selectedTicket.status === "In Progress" ? "orange" : 
                        selectedTicket.status === "Resolved" ? "green" : 
                        selectedTicket.status === "Closed" ? "gray" : "black",
                      fontWeight: "bold"
                    }}>
                      {selectedTicket.status}
                    </p>
                  </div>
                  
                  <div>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Priority:</label>
                    <p style={{ marginBottom: "15px" }}>{selectedTicket.priority}</p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Created By:</label>
                    <p style={{ marginBottom: "15px" }}>{selectedTicket.user_email}</p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Assignees:</label>
                    <p style={{ marginBottom: "15px" }}>
                      {Array.isArray(selectedTicket.assignees) 
                        ? selectedTicket.assignees.join(', ')
                        : typeof selectedTicket.assignees === 'string'
                          ? selectedTicket.assignees
                          : 'Unassigned'}
                    </p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Additional Users:</label>
                    <p style={{ marginBottom: "15px" }}>
                      {selectedTicket.additional_users && Array.isArray(selectedTicket.additional_users) && selectedTicket.additional_users.length > 0
                        ? selectedTicket.additional_users.join(', ')
                        : 'None'}
                    </p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Comments:</label>
                    <p style={{ 
                      marginBottom: "15px", 
                      whiteSpace: "pre-wrap", 
                      backgroundColor: "#f9f9f9", 
                      padding: "10px", 
                      borderRadius: "4px" 
                    }}>
                      {selectedTicket.comments || "No comments"}
                    </p>
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Project:</label>
                    <p style={{ marginBottom: "15px" }}>{selectedTicket.project || 'Not specified'}</p>
                  </div>
                </div>
                {/* Attachments Section for View Mode */}
                <div style={{ marginTop: "20px", paddingLeft: "20px", paddingRight: "20px" }}> 
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Attachments:</Typography>
                  {selectedTicket && selectedTicket.files && Array.isArray(selectedTicket.files) && selectedTicket.files.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {selectedTicket.files.map((file, index) => (
                        <AttachmentItem key={index} file={file} />
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No attachments</Typography>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} color="primary">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        
        {/* Edit Ticket Dialog */}
        <Dialog 
          open={openEditDialog} 
          onClose={() => setOpenEditDialog(false)}
          fullWidth
          maxWidth="md"
        >
          {editedTicket && (
            <>
              <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Edit Ticket #{editedTicket.ticket_number}</Typography>
                  <IconButton 
                    edge="end" 
                    color="inherit" 
                    onClick={() => setOpenEditDialog(false)} 
                    aria-label="close"
                  >
                    <CloseIcon />
                  </IconButton>
                </div>
              </DialogTitle>
              <DialogContent>
                <Box
                  component="form"
                  sx={{
                    '& .MuiTextField-root': { mb: 2 },
                    mt: 1
                  }}
                  noValidate
                  autoComplete="off"
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Ticket Number: {editedTicket.ticket_number}
                  </Typography>
                  
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="title"
                    label="Title"
                    name="title"
                    value={editedTicket.title || ''}
                    onChange={(e) => setEditedTicket({ ...editedTicket, title: e.target.value })}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="summary"
                    label="Summary"
                    name="summary"
                    value={editedTicket.summary || ''}
                    onChange={(e) => setEditedTicket({ ...editedTicket, summary: e.target.value })}
                  />
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    multiline
                    rows={4}
                    id="description"
                    label="Description"
                    name="description"
                    value={editedTicket.description || ''}
                    onChange={(e) => setEditedTicket({ ...editedTicket, description: e.target.value })}
                  />
                  {isAdminState ? (
                    <TextField
                      select
                      margin="normal"
                      required
                      fullWidth
                      id="status"
                      label="Status"
                      name="status"
                      value={editedTicket.status || 'Open'}
                      onChange={(e) => setEditedTicket({ ...editedTicket, status: e.target.value })}
                    >
                      <MenuItem value="Open">Open</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Resolved">Resolved</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </TextField>
                  ) : (
                    <TextField
                      margin="normal"
                      fullWidth
                      id="status-display"
                      label="Status"
                      name="status-display"
                      value={editedTicket.status || 'Open'}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  )}
                  <TextField
                    select
                    margin="normal"
                    required
                    fullWidth
                    id="priority"
                    label="Priority"
                    name="priority"
                    value={editedTicket.priority || 'Medium'}
                    onChange={(e) => setEditedTicket({ ...editedTicket, priority: e.target.value })}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Critical">Critical</MenuItem>
                  </TextField>
                  
                  {/* Only admins can edit assignees */}
                  {isAdminState ? (
                    <TextField
                      select
                      margin="normal"
                      fullWidth
                      id="assignees"
                      label="Assignees"
                      name="assignees"
                      value={Array.isArray(editedTicket.assignees) ? editedTicket.assignees : 
                            (editedTicket.assignees ? editedTicket.assignees.split(',') : [])}
                      onChange={(e) => {
                        // Ensure assignees is always an array
                        let assignees = e.target.value;
                        if (!Array.isArray(assignees)) {
                          assignees = assignees ? assignees.split(',').map(email => email.trim()).filter(email => email) : [];
                        }
                        
                        setEditedTicket({ 
                          ...editedTicket, 
                          assignees: assignees 
                        });
                      }}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} />
                            ))}
                          </Box>
                        ),
                      }}
                    >
                      {users.map((userItem) => (
                        <MenuItem key={userItem.email} value={userItem.email}>
                          {userItem.email}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Assignees: 
                      {Array.isArray(editedTicket.assignees) 
                        ? editedTicket.assignees.join(', ')
                        : typeof editedTicket.assignees === 'string'
                          ? editedTicket.assignees
                          : 'Unassigned'}
                    </Typography>
                  )}
                  
                  {/* Comments Section */}
                  <TextField
                    margin="normal"
                    fullWidth
                    multiline
                    rows={4}
                    id="comments"
                    label="Comments"
                    name="comments"
                    placeholder="Add any additional comments here..."
                    value={editedTicket.comments || ''}
                    onChange={(e) => setEditedTicket({ ...editedTicket, comments: e.target.value })}
                    InputProps={{
                      inputProps: { 'aria-label': 'Ticket comments' },
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button 
                  onClick={() => setOpenEditDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={saveEditedTicket}
                  variant="contained" 
                  color="primary"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        
        {/* Edit Ticket Popup */}
        <Dialog 
          open={showPopup} 
          onClose={closePopup}
          fullWidth
          maxWidth="md"
        >
          {editedTicket && (
            <>
              <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">{editMode ? "Edit Ticket" : "Ticket Details"} #{editedTicket.ticket_number}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(user && user.email === editedTicket.assignee) && (
                      <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => setEditMode(!editMode)}
                        sx={{ mr: 1 }}
                      >
                        {editMode ? 'Cancel' : 'Edit'}
                      </Button>
                    )}
                    <IconButton edge="end" color="inherit" onClick={closePopup} aria-label="close">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </div>
              </DialogTitle>
              <DialogContent dividers>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                  <div>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Title:</label>
                    {editMode ? (
                      <TextField
                        value={editedTicket.title || ''}
                        onChange={(e) => handleEditChange('title', e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <p style={{ marginBottom: "15px" }}>{editedTicket.title}</p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Description:</label>
                    {editMode ? (
                      <TextField
                        value={editedTicket.description || ''}
                        onChange={(e) => handleEditChange('description', e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={4}
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <p style={{ 
                        marginBottom: "15px", 
                        whiteSpace: "pre-wrap", 
                        backgroundColor: "#f9f9f9", 
                        padding: "10px", 
                        borderRadius: "4px" 
                      }}>
                        {editedTicket.description}
                      </p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Status:</label>
                    {editMode ? (
                      <select 
                        value={editedTicket.status || 'Open'} 
                        onChange={(e) => handleEditChange('status', e.target.value)}
                        style={{ 
                          padding: "8px", 
                          borderRadius: "4px", 
                          border: "1px solid #ddd",
                          backgroundColor: 
                            editedTicket.status === "Open" ? "#e3f2fd" : 
                            editedTicket.status === "In Progress" ? "#fff8e1" : 
                            editedTicket.status === "Resolved" ? "#e8f5e9" : 
                            editedTicket.status === "Closed" ? "#eeeeee" : "#ffffff"
                        }}
                      >
                        <option value="Open">Open</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Closed">Closed</option>
                      </select>
                    ) : (
                      <p style={{ 
                        marginBottom: "15px",
                        color: 
                          editedTicket.status === "Open" ? "blue" : 
                          editedTicket.status === "In Progress" ? "orange" : 
                          editedTicket.status === "Resolved" ? "green" : 
                          editedTicket.status === "Closed" ? "gray" : "black",
                        fontWeight: "bold"
                      }}>
                        {editedTicket.status}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Priority:</label>
                    {editMode ? (
                      <select 
                        value={editedTicket.priority || 'Low'} 
                        onChange={(e) => handleEditChange('priority', e.target.value)}
                        style={{ 
                          padding: "8px", 
                          borderRadius: "4px", 
                          border: "1px solid #ddd",
                          width: "100%"
                        }}
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    ) : (
                      <p style={{ marginBottom: "15px" }}>{editedTicket.priority}</p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Project:</label>
                    {editMode ? (
                      <TextField
                        value={editedTicket.project || ''}
                        onChange={(e) => handleEditChange('project', e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <p style={{ marginBottom: "15px" }}>{editedTicket.project || 'Not specified'}</p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Assignees:</label>
                    {editMode ? (
                      <TextField
                        select
                        margin="normal"
                        fullWidth
                        id="assignees"
                        label="Assignees"
                        name="assignees"
                        value={Array.isArray(editedTicket.assignees) ? editedTicket.assignees : 
                              (editedTicket.assignees ? editedTicket.assignees.split(',') : [])}
                        onChange={(e) => {
                          // Ensure assignees is always an array
                          let assignees = e.target.value;
                          if (!Array.isArray(assignees)) {
                            assignees = assignees ? assignees.split(',').map(email => email.trim()).filter(email => email) : [];
                          }
                          
                          setEditedTicket({ 
                            ...editedTicket, 
                            assignees: assignees 
                          });
                        }}
                        SelectProps={{
                          multiple: true,
                          renderValue: (selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} />
                              ))}
                            </Box>
                          ),
                        }}
                      >
                        {users.map((userItem) => (
                          <MenuItem key={userItem.email} value={userItem.email}>
                            {userItem.email}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <p style={{ marginBottom: "15px" }}>
                        {Array.isArray(editedTicket.assignees) 
                          ? editedTicket.assignees.join(', ')
                          : typeof editedTicket.assignees === 'string'
                            ? editedTicket.assignees
                            : 'Unassigned'}
                      </p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Additional Users:</label>
                    {editMode ? (
                      <TextField
                        value={Array.isArray(editedTicket.additional_users) ? editedTicket.additional_users.join(', ') : ''}
                        onChange={(e) => handleEditChange('additional_users', e.target.value.split(',').map(email => email.trim()).filter(email => email))}
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%' }}
                        placeholder="Comma-separated email addresses"
                      />
                    ) : (
                      <p style={{ marginBottom: "15px" }}>
                        {editedTicket.additional_users && Array.isArray(editedTicket.additional_users) && editedTicket.additional_users.length > 0
                          ? editedTicket.additional_users.join(', ')
                          : 'None'}
                      </p>
                    )}
                    
                    <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", marginTop: "15px" }}>Comments:</label>
                    {editMode ? (
                      <TextField
                        value={editedTicket.comments || ''}
                        onChange={(e) => handleEditChange('comments', e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={4}
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <p style={{ 
                        marginBottom: "15px", 
                        whiteSpace: "pre-wrap", 
                        backgroundColor: "#f9f9f9", 
                        padding: "10px", 
                        borderRadius: "4px" 
                      }}>
                        {editedTicket.comments || "No comments"}
                      </p>
                    )}
                  </div>
                </div>
                
                <div style={{ marginTop: "20px" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Attachments:</label>
                  {editedTicket && editedTicket.files && Array.isArray(editedTicket.files) && editedTicket.files.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {editedTicket.files.map((file, index) => (
                        <AttachmentItem key={index} file={file} />
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>No attachments</Typography>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                {editMode ? (
                  <>
                    <Button onClick={() => setEditMode(false)} color="secondary">
                      Cancel
                    </Button>
                    <Button onClick={saveEditedTicket} color="primary" variant="contained">
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <Button onClick={closePopup} color="primary">
                    Close
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>
        
      </Box>
    </div>
  );
}
